import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, switchMap, throwError } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';

import { AuthService } from '@shared/services/auth/auth.service';
import { SessionService } from '@shared/services/auth/session.service';
import { LocalStorageService } from '@shared/services/core/local-storage.service';
import { UserInfoService } from '@shared/services/auth/user-info.service';
import { AppService } from '@shared/services/core/app.service';
import { LogService } from '@shared/services/core/log.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  totalRetries: number = 0;
  static isRefreshing: boolean = false;

  constructor(
    private authService: AuthService,
    private sessionService: SessionService,
    private localStorageService: LocalStorageService,
    private logService: LogService,
    private userInfoService: UserInfoService,
    private appService: AppService,
    private deviceDetectorService: DeviceDetectorService
  ) { }

  intercept(request: HttpRequest<unknown>, nextHandler: HttpHandler): Observable<HttpEvent<unknown>> {
    const authAPI = this.appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALTOKEN"]["ClientLogin"].PATH;
    const refreshTokenAPI = this.appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALREFRESHTOKEN"]["RefreshToken"].PATH;

    if (request.url.indexOf(authAPI) > -1 || request.url.indexOf(refreshTokenAPI) > -1) {
      return nextHandler.handle(request);
    }
    // look for exisitng session from route.
    const jti = this.sessionService.getJTIFromCurrentRoute();
    const tokenPresent = this.sessionService.getToken(jti);
    const onMobileDevices = this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
    const mobileDeviceSpecificHeader = 'X-Er-Type';
    if(!tokenPresent) return nextHandler.handle(request);

    if (tokenPresent) {
      const isNewTokenRequired = false; //this.sessionService.isNewTokenRequired(tokenPresent);
      let originalReq: HttpRequest<unknown>;
      if (!isNewTokenRequired) {
        if (this.appService.getAppSettings().bypassSecurity) {
          originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
        }
        else {
          if (onMobileDevices)
            originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
          else 
            originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`)});
        }

        const userType: string = this.localStorageService.getLoggedInUserType();
        const userID: string = (userType === "CLIENT") ? this.localStorageService.getClientInfo('ClientID') : this.localStorageService.getProfessionalInfo("ProfessionalID");

        const request_data = JSON.stringify(originalReq);
        this.logService.info('Using Exisitng Token', { jti, user_id: userID, request_data, request_endpoint: request.url }, onMobileDevices, tokenPresent, jti);
        return nextHandler.handle(originalReq);
      } else {
        if (AuthInterceptor.isRefreshing) {
          if (this.appService.getAppSettings().bypassSecurity) {
            originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
          }
          else {
            if (onMobileDevices)
              originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
            else 
              originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`)});
          }

          const request_data = JSON.stringify(originalReq);
          this.logService.info('Refreshing Token', { jti, request_data, request_endpoint: request.url }, onMobileDevices, tokenPresent, jti);
          return nextHandler.handle(originalReq);
        }
        else {
          AuthInterceptor.isRefreshing = true;
          let currentRefreshToken = this.localStorageService.getRefreshToken() || '';
          if (!currentRefreshToken) {
            this.localStorageService.initializeData(jti);
            currentRefreshToken = this.localStorageService.storageData?.refreshToken || '';
          }

          return this.authService.getRefreshedToken(tokenPresent, currentRefreshToken).pipe(
            switchMap(refreshTokenResponse => {
              if (refreshTokenResponse?.httpStatusCode === 200) {
                let refreshToken = refreshTokenResponse.data.refreshToken;
                let tokenProvided = refreshTokenResponse.data.token;
                tokenProvided = refreshTokenResponse.data.token;

                this.sessionService.updateTokenAndRefreshTokenOnStorage(tokenProvided, refreshToken);
  
                AuthInterceptor.isRefreshing = false;

                if (this.appService.getAppSettings().bypassSecurity) {
                  originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
                }
                else {
                  if (onMobileDevices)
                    originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`).set(mobileDeviceSpecificHeader, jti)});
                  else 
                    originalReq = request.clone({ headers: request.headers.set('Authorization', `Bearer ${tokenPresent}`)});
                }

                const request_data = JSON.stringify(originalReq);
                this.logService.info("Using New Token", { jti, request_data, request_endpoint: originalReq.url }, onMobileDevices, tokenPresent, jti);
                return nextHandler.handle(originalReq);
              }
              else {
                this.userInfoService.logoutUser();
                return throwError(() => 'Invalid call');
              }
            })
          );
        }
      }
    }

    this.userInfoService.logoutUser();
    return throwError(() => 'Invalid call');
  }
}